import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { ClientContextDto } from '../dtos/common/client-context.dto';
import { isDevelopment, isLocal } from '../utils/util-helper';

@Injectable({ providedIn: 'root' })
export class ClientContextService {
  private translate = inject(TranslateService);

  buildClientContext(): ClientContextDto {
    const ua = navigator.userAgent ?? '';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const osVersion = detectOsVersionFromUserAgent(ua);
    const clientLabel = buildWebClientLabel(ua);

    let context: ClientContextDto = {
      clientType: 'web',
      platform: 'web',
      clientAppVersion: environment.appVersion ?? undefined,
      clientBuildNumber: environment.buildNumber ?? undefined,
      clientCommitSha: environment.commitSha ?? undefined,
      locale: this.getWebLocale(),
      timeZone: tz || undefined,
      clientLabel: buildWebClientLabel(ua),
      deviceInfo: {
        platform: 'web',
        deviceModel: 'Browser', // canonical key
        manufacturer: undefined, // web doesn’t have one
        osVersion, // e.g. "Windows 11", "macOS"
        isPhysicalDevice: true, // browsers always run on a real device
        clientLabel,
        userAgent: ua
      }
    };

    if (isLocal() || isDevelopment()) {
      console.log(`[buildClientContext()] = ${context}`);
    }

    return context;
  }

  /**
   * Returns the currently active application language.
   * This reflects user choice, not browser defaults.
   */
  private getWebLocale(): string | undefined {
    // 1️⃣ Preferred: currently active language
    if (this.translate.currentLang) {
      return this.translate.currentLang;
    }

    // 2️⃣ Fallback: default language configured in the app
    if (this.translate.defaultLang) {
      return this.translate.defaultLang;
    }

    // 3️⃣ Last resort: browser language
    return navigator.language || undefined;
  }
}

/**
 * Same lightweight label helper as before.
 */
function buildWebClientLabel(userAgent: string): string | undefined {
  if (!userAgent) return undefined;

  const ua = userAgent.toLowerCase();

  const browser = ua.includes('edg/')
    ? 'Edge'
    : ua.includes('opr/') || ua.includes('opera')
      ? 'Opera'
      : ua.includes('chrome/') && !ua.includes('chromium')
        ? 'Chrome'
        : ua.includes('safari/') && !ua.includes('chrome/')
          ? 'Safari'
          : ua.includes('firefox/')
            ? 'Firefox'
            : 'Browser';

  const os = ua.includes('windows nt')
    ? 'Windows'
    : ua.includes('mac os x') && !ua.includes('iphone') && !ua.includes('ipad')
      ? 'macOS'
      : ua.includes('iphone')
        ? 'iPhone'
        : ua.includes('ipad')
          ? 'iPad'
          : ua.includes('android')
            ? 'Android'
            : ua.includes('linux')
              ? 'Linux'
              : 'Unknown OS';

  return `${browser} on ${os}`;
}

function detectOsVersionFromUserAgent(ua: string): string | undefined {
  const s = ua.toLowerCase();

  if (s.includes('windows nt 11')) return 'Windows 11';
  if (s.includes('windows nt 10')) return 'Windows 10';
  if (s.includes('mac os x')) return 'macOS';
  if (s.includes('android')) return 'Android';
  if (s.includes('iphone') || s.includes('ipad')) return 'iOS';

  return undefined;
}
